
import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Spinner } from "react-bootstrap";
import { FaGoogle, FaMicrosoft } from "react-icons/fa";
import useLocalStorageState from "use-local-storage-state";
import GoogleCalendar from "./GoogleCalender";
import OutlookCalendar from "./Outlookcalender";
import { checkOutlookStatus } from "./outlookClient";

const ACTIVE_SERVICE_KEY = "calendar_active_service";
const GOOGLE_TOKEN_KEY = "google_access_token";
const GOOGLE_TOKEN_EXPIRY_KEY = "google_access_token_expires_at";
const OUTLOOK_CONNECTED_KEY = "outlook_connected";

function isGoogleConnected() {
  const token = localStorage.getItem(GOOGLE_TOKEN_KEY);
  if (!token) return false;
  const expiry = localStorage.getItem(GOOGLE_TOKEN_EXPIRY_KEY);
  if (expiry && Number(expiry) < Date.now()) return false;
  return true;
}

function cachedOutlookConnected() {
  return localStorage.getItem(OUTLOOK_CONNECTED_KEY) === "true";
}


function normalizeOutlookStatus(result) {
  if (result && typeof result === "object") {
    return { ...result, connected: !!result.connected };
  }
  return { connected: !!result };
}

export default function CalendarContainer() {
  const [customer] = useLocalStorageState("customer", { token: {} });
  const userId = customer?.customer_details?.id;
  const [googleOk, setGoogleOk] = useState(isGoogleConnected);
  const [outlookOk, setOutlookOk] = useState(cachedOutlookConnected);
  const [activeService, setActiveService] = useState(null);
  const [checking, setChecking] = useState(true);

  const pickActiveService = useCallback((g, o) => {
    if (o) return "outlook";
    if (g) return "google";
    return null;
  }, []);

  const verify = useCallback(async () => {
    const g = isGoogleConnected();

    let outlookStatus = { connected: false };
    if (userId) {
      try {
        outlookStatus = normalizeOutlookStatus(await checkOutlookStatus(userId));
      } catch {
        outlookStatus = { connected: false };
      }
    }
    const o = outlookStatus.connected;

    if (o) {
      localStorage.setItem(OUTLOOK_CONNECTED_KEY, "true");
    } else {
      localStorage.removeItem(OUTLOOK_CONNECTED_KEY);
    }

    setGoogleOk(g);
    setOutlookOk(o);
    setActiveService(pickActiveService(g, o));
    setChecking(false);
  }, [userId, pickActiveService]);

  useEffect(() => {
    if (userId === undefined) return;
    verify();
  }, [userId, verify]);

  useEffect(() => {
    window.addEventListener("focus", verify);
    window.addEventListener("storage", verify);
    return () => {
      window.removeEventListener("focus", verify);
      window.removeEventListener("storage", verify);
    };
  }, [verify]);

  const chooseService = useCallback((service) => {
    localStorage.setItem(ACTIVE_SERVICE_KEY, service);
    setActiveService(service);
  }, []);

  const handleLogout = useCallback(() => {
    verify();
  }, [verify]);

  if (checking) {
    return (
      <div
        className="p-4 d-flex align-items-center justify-content-center"
        style={{ backgroundColor: "#f5f6fa", minHeight: "90vh" }}
      >
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Checking connected calendars…</span>
      </div>
    );
  }

  if (!activeService) {
    return (
      <div className="p-4" style={{ backgroundColor: "#f5f6fa", minHeight: "90vh" }}>
        <Row className="mb-3">
          <Col>
            <h5>No calendar account connected</h5>
          </Col>
        </Row>
        <Card className="p-4">
          <Row className="text-center">
            <Col>
              <div
                onClick={() => chooseService("google")}
                className="border border-1 br-10 p-3 clickable d-flex align-items-start cursor-pointer"
              >
                <div className="flex-shrink-0">
                  <FaGoogle size={40} color="#4285F4" />
                </div>
                <div className="ms-3 d-flex flex-column text-start">
                  <h6>Google Calendar</h6>
                  <span className="text-muted" style={{ fontSize: "14px", lineHeight: "1.4" }}>
                    Sync events directly with your Google account. Create, edit,
                    and manage meetings without leaving the CRM.
                  </span>
                </div>
              </div>
            </Col>
            <Col>
              <div
                onClick={() => chooseService("outlook")}
                className="border border-1 br-10 p-3 clickable d-flex align-items-start cursor-pointer"
              >
                <div className="flex-shrink-0">
                  <FaMicrosoft size={40} color="#0078d4" />
                </div>
                <div className="ms-3 d-flex flex-column text-start">
                  <h6 className="fw-400 mb-1 mt-1">Outlook Calendar</h6>
                  <span className="text-muted" style={{ fontSize: "14px", lineHeight: "1.4" }}>
                    Sync events with Microsoft Outlook / Teams. Schedule and
                    manage meetings straight from your CRM dashboard.
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      </div>
    );
  }
  return activeService === "outlook" ? (
    <OutlookCalendar onLogout={handleLogout} setActiveService={setActiveService} />
  ) : (
    <GoogleCalendar onLogout={handleLogout} setActiveService={setActiveService} />
  );
}